import { supabase } from "../supabaseClient";

// Detecta HEIC
const isHeic = (f) => {
  const name = (f.name || "").toLowerCase();
  return (f.type && /heic|heif/.test(f.type)) || /\.heic$|\.heif$/.test(name);
};

// =============================
// COMPRESSÃO WEBP
// =============================
export async function compressImageToWebP(
  file,
  {
    maxDimension = 840,
    maxSizeBytes = 250 * 1024,
    initialQuality = 0.9,
    minQuality = 0.4,
    qualityStep = 0.08,
  } = {},
) {
  if (!file) return file;

  if (!isHeic(file) && (!file.type || !file.type.startsWith("image/")))
    return file;

  // HEIC → WEBP
  if (isHeic(file)) {
    try {
      const mod = await import("heic2any");
      const heic2any = mod.default || mod;

      const converted = await heic2any({
        blob: file,
        toType: "image/webp",
        quality: 0.9,
      });

      const blob = converted instanceof Blob ? converted : converted[0];

      file = new File([blob], `${Date.now()}.webp`, {
        type: "image/webp",
      });
    } catch (err) {
      console.warn("Falha ao converter HEIC:", err);
    }
  }

  // Carregar imagem
  const img = await new Promise((resolve, reject) => {
    const i = new Image();
    i.onload = () => resolve(i);
    i.onerror = reject;
    i.src = URL.createObjectURL(file);
  });

  const ratio = Math.min(1, maxDimension / Math.max(img.width, img.height));
  const width = img.width * ratio;
  const height = img.height * ratio;

  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;

  const ctx = canvas.getContext("2d");
  ctx.drawImage(img, 0, 0, width, height);

  const toBlob = (q) =>
    new Promise((res) => canvas.toBlob(res, "image/webp", q));

  let finalBlob = null;

  for (let q = initialQuality; q >= minQuality; q -= qualityStep) {
    const blob = await toBlob(q);
    if (!blob) continue;

    finalBlob = blob;
    if (blob.size <= maxSizeBytes) break;
  }

  if (!finalBlob) throw new Error("Erro ao gerar WebP");

  return new File([finalBlob], `${Date.now()}.webp`, {
    type: "image/webp",
  });
}

// =============================
// UPLOAD STORAGE
// =============================
export async function uploadToStorage(
  file,
  folderType = "uploads",
  options = {},
) {
  const DEFAULT_BUCKET = import.meta.env.VITE_STORAGE_DEFAULT_BUCKET || "cadastros";
  const BABA_PHOTO_BUCKET =
    import.meta.env.VITE_BABA_PHOTO_BUCKET || DEFAULT_BUCKET;
  const FAMILIA_PHOTO_BUCKET =
    import.meta.env.VITE_FAMILIA_PHOTO_BUCKET || DEFAULT_BUCKET;

  const bucketMap = {
    baba: BABA_PHOTO_BUCKET,
    familia: FAMILIA_PHOTO_BUCKET,
    qrcode: DEFAULT_BUCKET,
    "doc-frente": DEFAULT_BUCKET,
    "doc-verso": DEFAULT_BUCKET,
    "doc-selfie": DEFAULT_BUCKET,
    certificados: DEFAULT_BUCKET,
    documentos: DEFAULT_BUCKET,
    "documento-familia": DEFAULT_BUCKET,
    "documentos-familia": DEFAULT_BUCKET,
    // Aliases de segurança para evitar fallback caso o nome real seja usado
    "foto-documento-frente": DEFAULT_BUCKET,
    "foto-documento-verso": DEFAULT_BUCKET,
    "self-documento": DEFAULT_BUCKET,

    // ✅ PADRONIZADO
    "Fundo-pacote-horas": DEFAULT_BUCKET,
  };

  const bucket = bucketMap[folderType] || DEFAULT_BUCKET;

  console.log("📦 Bucket:", bucket);

  // =============================
  // COMPRESSÃO
  // =============================
  let fileToUpload = file;

  try {
    if (
      file &&
      ((file.type && file.type.startsWith("image/")) || isHeic(file))
    ) {
      let compressOpts = { maxDimension: 840, maxSizeBytes: 250 * 1024 };

      if (folderType === "qrcode") {
        compressOpts = { maxDimension: 512, maxSizeBytes: 60 * 1024 };
      }

      if (folderType === "Fundo-pacote-horas") {
        compressOpts = { maxDimension: 1200, maxSizeBytes: 150 * 1024 };
      }

      if (folderType === "certificados") {
        compressOpts = { maxDimension: 1000, maxSizeBytes: 125 * 1024 };
      }

      fileToUpload = await compressImageToWebP(file, compressOpts);
    }
  } catch (err) {
    console.warn("Erro compressão, enviando original:", err);
  }

  // =============================
  // AUTENTICAÇÃO
  // =============================
  // getSession() lê a sessão local (pode ser null em flows OAuth ou após refresh).
  // Fallback para getUser() que verifica no servidor e sempre retorna o usuário atual.
  let resolvedUserId = options.userId ?? null;

  const { data: sessionData } = await supabase.auth.getSession();
  let user = sessionData?.session?.user;

  if (!resolvedUserId && !user) {
    const { data: userData } = await supabase.auth.getUser();
    user = userData?.user ?? null;
  }

  resolvedUserId = resolvedUserId || user?.id || null;

  if (!resolvedUserId) {
    throw new Error("Usuário não autenticado.");
  }

  // =============================
  // NOME DO ARQUIVO
  // =============================
  const ext = fileToUpload.name.split(".").pop() || "webp";
  const fileName = `${Date.now()}-${Math.random()
    .toString(36)
    .substring(2, 8)}.${ext}`;

  // =============================
  // PATH (CORRIGIDO)
  // =============================
  let filePath;
  let subFolder = folderType;

  // Para documentos de identidade da babá, mantém pasta estável para compatibilidade de leitura/RLS
  if (["doc-frente", "doc-verso", "doc-selfie"].includes(folderType)) {
    subFolder = "baba";
  }

  if (folderType === "Fundo-pacote-horas") {
    filePath = fileName; // ✅ SEM duplicar pasta
  } else if (folderType === "qrcode") {
    filePath = fileName;
  } else {
    filePath = `${resolvedUserId}/${subFolder}/${fileName}`;
  }

  console.log("📁 Path:", filePath);

  // =============================
  // UPLOAD
  // =============================
  console.log(
    `DEBUG [Storage]: Tentando upload no bucket '${bucket}' com path '${filePath}'...`,
  );
  const { error } = await supabase.storage
    .from(bucket)
    .upload(filePath, fileToUpload, {
      upsert: true,
      contentType: fileToUpload.type || "image/webp",
    });

  if (error) {
    console.error("Erro upload:", error);
    throw new Error(error.message);
  }

  // =============================
  // URL PÚBLICA
  // =============================
  const { data: urlData } = supabase.storage
    .from(bucket)
    .getPublicUrl(filePath);

  if (!urlData?.publicUrl) {
    throw new Error("Erro ao gerar URL pública");
  }

  return {
    publicUrl: urlData.publicUrl,
    path: filePath,
    fileName,
  };
}
